import { log } from 'console';
import QRCode, { QRCodeToDataURLOptions } from 'qrcode';
import React from 'react';

export const generateQrCode = async (text: string) => {
  const options: QRCodeToDataURLOptions = {
    margin: 0,
    width: 300,
    color: {
      dark: '#000',
      light: '#fff',
    },
  };
  return QRCode.toDataURL(text, options);
};

interface ComponentData {
  type: string;
  visible?: string;
  props?: Record<string, any>;
  children?: ComponentData[] | ComponentData | string | number;
}

interface ComponentMap {
  [key: string]: React.ComponentType<any>;
}

// Recursive function to render components from nested data structure
export function renderComponent(args: {
  componentData: ComponentData | ComponentData[] | string | number;
  components: ComponentMap;
  data: any;
  allProps: Record<string, any>;
  images: any;
}): any {
  const { componentData, components, data, allProps = {}, images = {} } = args;

  if (allProps.key) delete allProps.key;
  if (allProps.styles) delete allProps.styles;
  if (allProps.index) delete allProps.index;
  if (allProps.className) delete allProps.className;
  if (allProps.children) delete allProps.children;

  // Handle array of components
  if (Array.isArray(componentData) && componentData.length > 0) {
    return componentData.map((item, index) =>
      renderComponent({
        componentData: { ...item, props: { ...item.props, index } },
        components,
        data,
        allProps: {},
        images,
      }),
    );
  }

  // Handle single component object
  if (
    typeof componentData === 'object' &&
    componentData !== null &&
    'type' in componentData &&
    Object.keys(components).includes(componentData.type)
  ) {
    const { type, props = {}, children, visible = 'true' } = componentData;
    const mergedProps = { ...allProps, ...props };

    // Get the component constructor/function from the components object
    const Component = components[type];

    if (!Component) {
      console.warn(`Component type "${type}" not found in components object`);
      return null;
    }

    // Process children recursively
    let processedChildren: any = null;

    if (children !== undefined) {
      if (Array.isArray(children)) {
        // If children is an array, recursively process each child
        processedChildren = children.map((child, index) =>
          renderComponent({
            componentData: child,
            components,
            data,
            allProps: mergedProps,
            images,
          }),
        );
      } else if (
        typeof children === 'object' &&
        children !== null &&
        'type' in children
      ) {
        // If children is a single component object, process it
        processedChildren = renderComponent({
          componentData: children,
          components,
          data,
          allProps: mergedProps,
          images,
        });
      } else {
        // If children is a string or other primitive, use it directly
        processedChildren = children;
      }
    }

    // Return the component with processed children
    const propsWithKey: Record<string, any> = {
      ...mergedProps,
      key: crypto.randomUUID(),
    };

    if (type === 'Map') {
      propsWithKey.data = data;
      propsWithKey.images = images;
    }

    // const mappedProps = mapDataProps(propsWithKey, data);
    const { mappedChild, mappedIsVisible, ...mappedProps } = mapDataProps(
      {
        ...propsWithKey,
        mappedChild: processedChildren,
        mappedIsVisible: visible,
      },
      data,
      images,
    );

    const canRender = safeBooleanEval(mappedIsVisible);
    return canRender
      ? React.createElement(Component, mappedProps, mappedChild)
      : null;
  }

  // Handle primitive values (strings, numbers, etc.)
  return componentData;
}

export const mapDataProps = (
  props: Record<string, any>,
  data: any,
  images: any,
) => {
  const context = { $data: data, $props: props, $images: images };
  const templateRegex = /\{\{([^}]+)\}\}/g;

  const processStringValue = (value: string): any => {
    const matches = Array.from(value.matchAll(templateRegex));

    if (matches.length === 0) {
      return value;
    }

    // Single template that spans the entire string - return the actual value
    if (matches.length === 1 && matches[0][0] === value) {
      const path = matches[0][1].trim();
      const resolvedValue = getNestedProperty({ obj: context, path });
      return resolvedValue !== undefined ? resolvedValue : value;
    }

    // Multiple templates or partial string replacement - always return string
    return matches.reduce((result, match) => {
      const path = match[1].trim();
      const resolvedValue = getNestedProperty({ obj: context, path });
      const replacement =
        resolvedValue !== undefined ? String(resolvedValue) : match[0];
      return result.replace(match[0], replacement);
    }, value);
  };

  return Object.fromEntries(
    Object.entries(props).map(([key, value]) => [
      key,
      typeof value === 'string' ? processStringValue(value) : value,
    ]),
  );
};

export const parseJson = (jsonString: string) => {
  try {
    const obj = JSON.parse(jsonString);
    return obj;
  } catch (e) {
    return null;
  }
};

export function getNestedProperty({
  obj,
  path,
}: {
  obj?: any;
  path: string;
}): any {
  // Split the path by dots and filter out empty strings
  const keys = path.split('.').filter((key) => key.length > 0);

  // Start with the root object
  let current = obj;

  // Traverse through each key in the path
  for (const key of keys) {
    // Check if current is null/undefined or if the key doesn't exist
    if (current == null || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }

  if (typeof current === 'object') {
    return JSON.stringify(current);
  }
  return current;
}

export function safeBooleanEval(code: string) {
  try {
    const result = eval(code);
    return typeof result === 'boolean' ? result : false;
  } catch (error) {
    console.error(`Error evaluating code "${code}":`, error);
    return false;
  }
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const content = event.target?.result;
      if (typeof content === 'string') {
        resolve(content);
      } else {
        reject(new Error('File content is not a string'));
      }
    };

    reader.onerror = function (error) {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}
